import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { statusToneIconClass, statusToneSubtleClass, type StatusTone } from '@/lib/statusTone';
import { Icon, type IconName } from '@/components/ui/icon';

/**
 * Chip Component
 *
 * Compact status/tag component with semantic coloring.
 * Leverages centralized status tone helpers for consistent styling.
 *
 * Features:
 * - Tone variants (neutral, success, warning, danger, info)
 * - Optional icon support
 * - Fixed color map for consistent status display
 * - Small size by default (12px text)
 *
 * @see CLAUDE.md for usage guidelines
 * @example
 * ```tsx
 * <Chip tone="success" icon="check_circle">
 *   Finalizzato
 * </Chip>
 *
 * <Chip tone="danger">
 *   Bloccato
 * </Chip>
 * ```
 */

export interface ChipProps {
  /** Content to display inside the chip */
  children: ReactNode;

  /** Color tone for semantic meaning */
  tone?: StatusTone;

  /** Size variant */
  size?: 'sm';

  /** Optional icon name (displayed on left) */
  icon?: IconName;

  /** Additional CSS classes */
  className?: string;
}

export function Chip({
  children,
  tone = 'neutral',
  size = 'sm', // Currently only 'sm' supported, reserved for future expansion
  icon,
  className,
}: ChipProps) {
  // Size parameter reserved for future size variants
  void size;
  const toneClass = statusToneSubtleClass(tone);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium uppercase tracking-wide',
        toneClass,
        className
      )}
    >
      {icon && (
        <Icon name={icon} className={cn('icon-sm', statusToneIconClass(tone))} aria-hidden="true" />
      )}
      {children}
    </span>
  );
}

/**
 * Status-specific chip variants with fixed color mapping
 */

export type ReportStatus = 'finalized' | 'draft';
export type ActionStatus = 'inProgress' | 'blocked' | 'dueSoon';

const STATUS_CHIP_MAP: Record<string, { tone: StatusTone; label: string; icon: IconName }> = {
  finalized: {
    tone: 'success' as StatusTone,
    label: 'Finalizzato',
    icon: 'task_alt',
  },
  draft: { tone: 'neutral' as StatusTone, label: 'Bozza', icon: 'edit_note' },
  inProgress: {
    tone: 'info' as StatusTone,
    label: 'In corso',
    icon: 'pending',
  },
  blocked: { tone: 'danger' as StatusTone, label: 'Bloccato', icon: 'block' },
  dueSoon: {
    tone: 'warning' as StatusTone,
    label: 'In scadenza',
    icon: 'schedule',
  },
} as const;

export interface StatusChipProps {
  /** Status type */
  status: ReportStatus | ActionStatus | keyof typeof STATUS_CHIP_MAP;

  /** Show icon */
  showIcon?: boolean;

  /** Additional CSS classes */
  className?: string;
}

/**
 * StatusChip Component
 *
 * Pre-configured chip for common status values.
 * Ensures consistent labeling and coloring across the app.
 *
 * @example
 * ```tsx
 * <StatusChip status="finalized" showIcon />
 * <StatusChip status="draft" />
 * ```
 */
export function StatusChip({ status, showIcon = false, className }: StatusChipProps) {
  const config = STATUS_CHIP_MAP[status as keyof typeof STATUS_CHIP_MAP];

  if (!config) {
    console.warn(`Unknown status: ${status}`);
    return (
      <Chip tone="neutral" className={className}>
        {status}
      </Chip>
    );
  }

  return (
    <Chip tone={config.tone} icon={showIcon ? config.icon : undefined} className={className}>
      {config.label}
    </Chip>
  );
}

/**
 * Helper function to get status chip configuration
 * Useful for other components that need status colors/labels
 */
export function getStatusConfig(
  status: ReportStatus | ActionStatus | keyof typeof STATUS_CHIP_MAP
) {
  return (
    STATUS_CHIP_MAP[status as keyof typeof STATUS_CHIP_MAP] || {
      tone: 'neutral' as const,
      label: status,
      icon: undefined,
    }
  );
}
